import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Chat } from '@tink/core/src/components/atoms/Icons/Chat';
import { Envelope } from '@tink/core/src/components/atoms/Icons/Envelope';
import { Phone } from '@tink/core/src/components/atoms/Icons/Phone';
import { PersonalCode } from '@tink/core/src/components/atoms/Icons/PersonalCode';
import { ProviderField } from '../../../models/provider';

import { H1 } from '../../atoms/Heading/Heading';
import { CircledIcon } from '../../atoms/Icons/CircledIcon';
import { SelectableOption } from '../../screens/molecules/SelectableOption/SelectableOption';
import { SubmitProviderInputsPayload } from '../../screens/SubmitCredentials/molecules/ProviderInputs/ProviderInputs';
import { ContentBody } from '../../atoms/ContentBody/ContentBody';
import { Text } from '../../atoms/Text/Text';
import { TemplateElementStyle, TemplateElementType } from '../templates';
import { messages } from './messages';

export type OtpMethod = 'text' | 'call' | 'email' | 'unknown';

export const getOtpIcon = (method: OtpMethod) => {
  if (method === 'call') {
    return Phone;
  }

  if (method === 'text') {
    return Chat;
  }

  if (method === 'email') {
    return Envelope;
  }

  return PersonalCode;
};

export type OtpMethodsTemplateViewProps = {
  fields: ProviderField[];
  handleSubmit: (values: SubmitProviderInputsPayload) => void;
};

export const OtpMethodsTemplateView = ({ fields, handleSubmit }: OtpMethodsTemplateViewProps) => {
  const intl = useIntl();

  const parsedElements = useMemo(() => {
    const title = fields.find(
      (el: ProviderField) =>
        el.type === TemplateElementType.TEXT && el.style === TemplateElementStyle.TITLE
    );

    const selectOptions = fields.find((el) => el.selectOptions && el.selectOptions.length > 0);

    return { title, selectOptions };
  }, [fields]);

  const { title, selectOptions } = parsedElements;

  return (
    <ContentBody data-testid="otp-methods-template-screen">
      <H1>{title?.value ?? <FormattedMessage {...messages.title} />}</H1>

      <Text color="secondary">
        {title?.helpText ?? <FormattedMessage {...messages.subtitle} />}
      </Text>

      {selectOptions?.selectOptions &&
        selectOptions.selectOptions.map((selectOption) => {
          // the keys for SelectOption are fixed, so we need to exploit them.
          // iconUrl => type
          // text => anonymized data or copy
          const otpType = selectOption.iconUrl as OtpMethod;
          const otpHint = selectOption.text ?? '';
          const otpTitle = messages[otpType] ? intl.formatMessage(messages[otpType]) : '';

          return (
            <SelectableOption
              key={selectOption.value}
              text={otpHint}
              title={otpTitle}
              Icon={
                <CircledIcon Icon={getOtpIcon(otpType)} color="greyWhite" iconColor="primary" />
              }
              onClick={() => {
                handleSubmit({ [selectOptions.name]: selectOption.value });
              }}
            />
          );
        })}
    </ContentBody>
  );
};
